<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="600" persistent>
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title>{{ modo === 'camara' ? 'Tomar foto' : 'Recortar imagen' }}</v-toolbar-title>

        <v-spacer />

        <v-btn icon dark @click="cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0">
        <v-alert v-if="error" type="error" class="ma-3">{{ error }}</v-alert>

        <video ref="video" autoplay playsinline v-show="modo === 'camara'" style="width: 100%; display: block" />

        <canvas ref="canvas" style="display: none" />

        <div v-show="modo === 'recorte'" class="recorte-contenedor">
          <img
            ref="imagenRecorte"
            :src="imagenCapturadaUrl || ''"
            alt="Captura"
            style="max-width: 100%; display: block" />
        </div>
      </v-card-text>

      <v-card-actions class="justify-center pa-3 flex-wrap" style="gap: 8px">
        <template v-if="modo === 'camara'">
          <v-btn outlined color="primary" :disabled="capturando || !!error" @click="cambiarCamara">
            <v-icon left>mdi-camera-flip-outline</v-icon>
            Cambiar cámara
          </v-btn>

          <v-btn color="primary" :loading="capturando" :disabled="!!error" @click="capturar">
            <v-icon left>mdi-camera</v-icon>
            Capturar
          </v-btn>
        </template>

        <template v-else>
          <v-btn-toggle v-model="ratioActivo" mandatory dense color="primary" class="mb-2">
            <v-btn
              v-for="r in ratios"
              :key="r.value"
              :value="r.value"
              small
              :color="ratioActivo === r.value ? 'primary' : ''"
              :style="{ color: ratioActivo === r.value ? 'white' : 'var(--v-theme-primary)' }">
              {{ r.label }}
            </v-btn>
          </v-btn-toggle>

          <div class="d-flex" style="gap: 8px; width: 100%; justify-content: center">
            <v-btn outlined color="primary" @click="volverACamara">
              <v-icon left>mdi-camera-retake</v-icon>
              Volver a tomar
            </v-btn>

            <v-btn color="primary" @click="confirmarRecorte">
              <v-icon left>mdi-crop</v-icon>
              Confirmar
            </v-btn>
          </div>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.min.css';

export default {
  name: 'CamaraCaptura',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      stream: null,
      facingMode: 'environment',
      capturando: false,
      error: null,
      modo: 'camara',
      imagenCapturadaUrl: null,
      cropper: null,
      ratioActivo: 'libre',
      ratios: [
        { label: 'Libre', value: 'libre' },
        { label: '16:9', value: '16:9' },
        { label: '9:16', value: '9:16' },
        { label: '1:1', value: '1:1' },
      ],
    };
  },
  watch: {
    value(nuevoValor) {
      if (nuevoValor) {
        this.$nextTick(() => this.iniciarCamara());
      } else {
        this.detenerCamara();
        this.destroyCropper();
      }
    },
    modo(nuevoModo) {
      if (nuevoModo === 'recorte') {
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.initCropper();
          });
        });
      }
    },
    ratioActivo(nuevoRatio) {
      if (this.cropper) {
        this.cropper.setAspectRatio(this.ratioParaCropperjs(nuevoRatio));
      }
    },
  },
  methods: {
    async iniciarCamara() {
      this.error = null;

      try {
        this.detenerCamara();
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: this.facingMode } },
        });
        this.$refs.video.srcObject = this.stream;
      } catch (e) {
        this.error = 'No se pudo acceder a la cámara. Verifique los permisos.';
        window.console.error('getUserMedia error:', e);
      }
    },
    detenerCamara() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }

      if (this.$refs.video) {
        this.$refs.video.srcObject = null;
      }
    },
    async cambiarCamara() {
      this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
      await this.iniciarCamara();
    },
    capturar() {
      this.capturando = true;
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (this.imagenCapturadaUrl) {
            URL.revokeObjectURL(this.imagenCapturadaUrl);
          }
          this.imagenCapturadaUrl = URL.createObjectURL(blob);
          this.detenerCamara();
          this.modo = 'recorte';
          this.capturando = false;
        },
        'image/jpeg',
        0.92,
      );
    },
    initCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      this.cropper = new Cropper(this.$refs.imagenRecorte, {
        aspectRatio: this.ratioParaCropperjs(this.ratioActivo),
        viewMode: 1,
        autoCropArea: 0.9,
        responsive: true,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        toggleDragModeOnDblclick: false,
      });
    },
    ratioParaCropperjs(ratio) {
      return { '1:1': 1, '16:9': 16 / 9, '9:16': 9 / 16, libre: NaN }[ratio];
    },
    destroyCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      if (this.imagenCapturadaUrl) {
        URL.revokeObjectURL(this.imagenCapturadaUrl);
        this.imagenCapturadaUrl = null;
      }
    },
    volverACamara() {
      this.destroyCropper();
      this.modo = 'camara';
      this.$nextTick(() => this.iniciarCamara());
    },
    confirmarRecorte() {
      if (!this.cropper) return;
      const canvas = this.cropper.getCroppedCanvas({
        maxWidth: 4096,
        maxHeight: 4096,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      canvas.toBlob(
        (blob) => {
          const archivo = new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' });
          this.$emit('archivo-capturado', archivo);
          this.destroyCropper();
          this.cerrar();
        },
        'image/jpeg',
        0.92,
      );
    },
    cerrar() {
      this.detenerCamara();
      this.destroyCropper();
      this.modo = 'camara';
      this.$emit('input', false);
    },
  },
  beforeDestroy() {
    this.detenerCamara();
    this.destroyCropper();
  },
};
</script>

<style scoped>
.recorte-contenedor {
  width: 100%;
  max-height: 400px;
  overflow: hidden;
}
</style>
